import { formatMoney, type Locale, toBanglaDigits } from "@zeeta/shared";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { reportError } from "@/lib/report-error";
import { bn } from "./bn";
import { en, type Messages } from "./en";

type Leaves<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];
export type MessageKey = Leaves<Messages>;

const CATALOGS: Record<Locale, Messages> = { en, bn };
const STORAGE_KEY = "zeeta.locale";

function lookup(messages: Messages, key: string): string {
  let node: unknown = messages;
  for (const part of key.split(".")) {
    node = (node as Record<string, unknown> | undefined)?.[part];
  }
  return typeof node === "string" ? node : key;
}

interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Formats a number in the active locale's digits. */
  num: (value: number | string) => string;
  money: (poisha: number) => string;
  date: (epochMs: number) => string;
}

const I18nContext = createContext<I18n | null>(null);

function readStoredLocale(): Locale {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "en" || value === "bn" ? value : "bn";
  } catch {
    return "bn";
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage can be unavailable (private mode); the choice still applies for this session.
    }
  }, []);

  const value = useMemo<I18n>(() => {
    const messages = CATALOGS[locale];
    const num = (v: number | string) => (locale === "bn" ? toBanglaDigits(String(v)) : String(v));
    return {
      locale,
      setLocale,
      num,
      t: (key, vars) =>
        lookup(messages, key).replace(/\{(\w+)\}/g, (_, name: string) => {
          const v = vars?.[name];
          return v === undefined ? `{${name}}` : num(v);
        }),
      money: (poisha) => {
        // A missing amount (e.g. an old screen reading a newer server) shows a dash instead of
        // breaking the whole page; it is still reported so the cause gets fixed.
        if (!Number.isSafeInteger(poisha)) {
          reportError(new Error(`Not a valid poisha amount: ${String(poisha)}`));
          return "—";
        }
        return formatMoney(poisha, { digits: locale === "bn" ? "beng" : "latn" });
      },
      date: (epochMs) =>
        new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: "Asia/Dhaka",
        }).format(epochMs),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
